<nav aria-label={i18n.search_results_paging_navigation_section}>
    <div class="itsl-common-pager__container">
        <div>
            {#if pageCount > 1}
                <ul class="itsl-common-pager">
                    <li class="itsl-common-pager__item">
                        <IconButton
                            iconClass="itsl-common-pager__prev-button-icon"
                            id={`prevButton${guidV4()}`}
                            htmlType="button"
                            type="plain"
                            text={i18n.previous}
                            iconOnly={true}
                            disabled={prevButtonDisabled}
                            on:click={onPreviousButtonClick}
                        />
                    </li>
                {#each pagerItems as pagerItem}
                    <li class="itsl-common-pager__item">
                        <PagerItem
                            {...pagerItem}
                            {i18n}
                            on:pageSelected={onPageSelected}
                        />
                    </li>
                {/each}
                    <li class="itsl-common-pager__item">
                        <IconButton
                            iconClass="itsl-common-pager__next-button-icon"
                            id={`nextButton${guidV4()}`}
                            htmlType="button"
                            type="plain"
                            text={i18n.next}
                            iconOnly={true}
                            disabled={nextButtonDisabled}
                            on:click={onNextButtonClick}
                        />
                    </li>
                </ul>
            {/if}
        </div>
        <div class="itsl-common-pager__page-size-container">
            <div>
                <span id="itsl-common-pager-page-results" class="itsl-common-pager__page-number" aria-live={ariaLiveType}>
                {pagerText}
                </span>
            </div>
            <div class="itsl-common-pager__page-size-selector">
                <ExtendableSelector
                    id={`pageSizeSelector${guidV4()}`}
                    text={pageSize}
                    labelText={i18n.page_size}
                    labelHidden={true}
                    extendable={false}
                    position="up"
                    align="right"
                    disabled={loadingInProgress}
                    items={pageSizeItems}
                    selectedValue={pageSize}
                    on:selectedOption={onPageSizeChanged}
                />
            </div>
        </div>
    </div>
</nav>

<script>
    import IconButton from '@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte';
    import PagerItem from '../PagerItem/PagerItem.svelte';
    import ExtendableSelector from '../ExtendableSelector/ExtendableSelector.svelte';

    import { createEventDispatcher } from 'svelte';
    import { guidV4 } from '@itslearning/atlas/generate/guid';
    import { PAGE_SIZE_S, PAGE_SIZE_M, PAGE_SIZE_L } from '../utils/constants';
    import { formatLanguageString } from '@itslearning/atlas/strings/format';

    const PAGE_LINKS_NEAR_SELECTED_COUNT = 2;

    export let i18n;
    export let totalCount = 0;
    export let pageIndex = 0;
    export let loadingInProgress = false;
    export let pageSize = PAGE_SIZE_S;
    export let pagerItems = [];

    /** @type {'polite'|'assertive'|'off'} */
    export let ariaLiveType = 'polite';

    const dispatch = createEventDispatcher();

    $: pagerText = formatLanguageString(i18n.grid_PageInfoLabelTemplate,
            (pageSize * pageIndex) + 1,
            (pageIndex + 1) === pageCount
                ? totalCount
                : (pageSize * pageIndex) + pageSize,
            totalCount);

    $: prevButtonDisabled = loadingInProgress || pageIndex === 0;
    $: nextButtonDisabled = loadingInProgress || pageCount === 0 || pageIndex === pageCount - 1;
    $: pageSizeItems = [{
        options:
        [
            {
                label: formatLanguageString(i18n.view_X, PAGE_SIZE_S),
                value: PAGE_SIZE_S
            },
            {
                label: formatLanguageString(i18n.view_X, PAGE_SIZE_M),
                value: PAGE_SIZE_M
            },
            {
                label: formatLanguageString(i18n.view_X, PAGE_SIZE_L),
                value: PAGE_SIZE_L
            }
        ]
    }];

    $: numberOfFullPages = Math.trunc(totalCount / pageSize);
    $: pageCount = totalCount % pageSize > 0 ? numberOfFullPages + 1 : numberOfFullPages;
    $: {
        if (pageSize > 0 && totalCount > pageSize) {
            let ellipsisAdded = false;
            let items = [];

            const currentPage = pageIndex + 1;

            pagerItems = [];
            for (let pageStart = 1; pageStart <= pageCount; pageStart++) {
                if (pageStart !== currentPage &&
                    (pageStart === 1 || pageStart === pageCount || Math.abs(currentPage - pageStart) <= PAGE_LINKS_NEAR_SELECTED_COUNT)) {
                        items.push({
                        id: pageStart - 1,
                        text: i18n.page_X_of_Y
                            ? formatLanguageString(i18n.page_X_of_Y, pageStart, pageCount)
                            : '',
                        isSelected: false,
                        cssClass: Math.abs(currentPage - pageStart) === PAGE_LINKS_NEAR_SELECTED_COUNT
                            ? 'itsl-common-pager-item__button--not-mobile'
                            : ''
                    });
                } else if (pageStart === currentPage) {
                    items.push({
                        id: pageStart - 1,
                        text: i18n.page_X_of_Y
                            ? formatLanguageString(i18n.page_X_of_Y, pageStart, pageCount)
                            : '',
                        isSelected: true
                    });
                    ellipsisAdded = false;
                } else if (!ellipsisAdded) {
                    ellipsisAdded = true;

                    items.push({
                        id: -1,
                        text: '...',
                        isSelected: false
                    });
                }
            }

            pagerItems = items;
        }
    }

    export function getPagerText() {
        return pagerText;
    }

    function onPreviousButtonClick() {
        const newPageIndex = pageIndex - 1;

        pageIndex = newPageIndex;
        dispatch('pageChanged', { pageIndex: newPageIndex });
    }

    function onNextButtonClick() {
        const newPageIndex = pageIndex + 1;

        pageIndex = newPageIndex;
        dispatch('pageChanged', { pageIndex: newPageIndex });
    }

    function onPageSizeChanged({ detail }) {
        pageSize = detail.detail.data;
        pageIndex = 0;

        dispatch('pageSizeChanged', { pageIndex, pageSize });
    }

    function onPageSelected({ detail }) {
        pageIndex = detail.id;
        dispatch('pageChanged', { pageIndex });
    }
</script>
